import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/Badge'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { ErrorState, Loading, Notice } from '@/components/ui/States'
import { cn } from '@/lib/cn'
import { formatAgo, formatDateShort, today } from '@/lib/dates'
import { formatMoney, formatMoneyWhole, formatPercent, pluralise } from '@/lib/format'
import { PERIOD_LABEL, PERIODS, costPer, daysIn, periodRange, rankAds, spendByDay, totals, type Period } from '@/lib/marketing'
import { describeError } from '@/lib/supabase'
import {
  CHANNEL_LABEL,
  SOURCE_LABEL,
  useAdDays,
  useInstagramDays,
  useLatestReview,
  useMarketingBookings,
  useMarketingEnquiries,
  useMetaAds,
  type MetaAd,
} from './queries'

const PERIOD_KEY = 'marketing-period'

function storedPeriod(): Period {
  try {
    const value = localStorage.getItem(PERIOD_KEY)
    return (PERIODS as readonly string[]).includes(value ?? '') ? (value as Period) : 'month'
  } catch {
    return 'month'
  }
}

/**
 * Marketing dashboard (27 Oct 2026): what the ads cost and what they bring
 * in, beside Instagram and the enquiries and bookings that follow. The numbers
 * come from the marketing routine (scripts/marketing/marketing.mjs, every three
 * days), which also writes the review at the top. Every number follows the
 * period chosen, except "Spending now", which is today's budget.
 */
export function MarketingDashboardPage() {
  const onDate = today()
  const [period, setPeriodState] = useState<Period>(storedPeriod)
  const setPeriod = (next: Period) => {
    try {
      localStorage.setItem(PERIOD_KEY, next)
    } catch {
      // Private browsing: remembered for this visit only.
    }
    setPeriodState(next)
  }
  const { from, to } = periodRange(period, onDate)
  const month = periodRange('month', onDate)

  const ads = useMetaAds()
  const adDays = useAdDays(from, to)
  const monthDays = useAdDays(month.from, month.to)
  const instagram = useInstagramDays(from, to)
  const review = useLatestReview()
  const enquiries = useMarketingEnquiries(from, to)
  const bookings = useMarketingBookings(from, to)

  const adsById = useMemo(() => new Map((ads.data ?? []).map((ad) => [ad.ad_id, ad])), [ads.data])

  if (ads.isPending) return <Loading label="Loading marketing" />
  if (ads.isError) {
    return (
      <Page>
        <ErrorState title="Marketing could not load" detail={describeError(ads.error)} onRetry={() => void ads.refetch()} />
      </Page>
    )
  }

  const connected = ads.data.length > 0
  const sum = totals(adDays.data ?? [])
  const monthSpend = totals(monthDays.data ?? []).spend
  const ranked = rankAds(adDays.data ?? [])
  const lastSync = ads.data.reduce<string | null>((latest, ad) => (!latest || ad.synced_at > latest ? ad.synced_at : latest), null)

  const enquiryList = enquiries.data ?? []
  const bookingList = bookings.data ?? []
  const fromAds = enquiryList.filter((enquiry) => enquiry.lead_source === 'meta_ads').length
  const bookedFromAds = bookingList.filter((booking) => booking.inquiry?.lead_source === 'meta_ads').length

  return (
    <Page>
      <PageHeader
        eyebrow="Marketing"
        title="Dashboard"
        meta={
          <>
            <span>
              {formatDateShort(from)} to {formatDateShort(to)}
            </span>
            {lastSync ? <span>Ads updated {formatAgo(lastSync)}</span> : null}
          </>
        }
      />

      <div role="radiogroup" aria-label="Period" className="mb-5 flex flex-wrap gap-1.5">
        {PERIODS.map((key) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={period === key}
            onClick={() => setPeriod(key)}
            className={cn(
              'rounded-full px-3 py-1 text-[length:var(--text-sm)] transition-colors duration-[var(--dur-fast)]',
              period === key ? 'bg-accent font-semibold text-on-accent' : 'bg-paper text-taupe hover:text-ink',
            )}
          >
            {PERIOD_LABEL[key]}
          </button>
        ))}
      </div>

      {!connected ? (
        <Notice tone="neutral" title="Meta isn't connected yet" className="mb-5">
          The ad and Instagram numbers fill in once your Meta ad account is connected (one key saved on your Mac; Claude
          walks you through it). Enquiries and bookings below already count.
        </Notice>
      ) : null}

      {review.data ? <ReviewCard review={review.data} /> : null}

      {/* Ads at a glance */}
      <h2 className="mb-2.5 text-[length:var(--text-base)] font-semibold">Ads</h2>
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Stat label="Spending now" value={connected ? formatMoneyWhole(dailyBudget(ads.data)) : '–'} detail="a day, active ads" />
        <Stat label="Spent this month" value={connected ? formatMoneyWhole(monthSpend) : '–'} detail="so far, whatever the period" />
        <Stat
          label="Spent in period"
          value={connected ? formatMoneyWhole(sum.spend) : '–'}
          detail={sum.days ? `${formatMoneyWhole(sum.spend / sum.days)} a day over ${pluralise(sum.days, 'day')}` : PERIOD_LABEL[period].toLowerCase()}
        />
        <Stat
          label="Conversations and leads"
          value={connected ? String(sum.results) : '–'}
          detail={sum.results ? `${formatMoney(sum.spend / sum.results)} each` : 'DMs, WhatsApps and lead forms'}
        />
        <Stat
          label="Link clicks"
          value={connected ? sum.link_clicks.toLocaleString('en-ZA') : '–'}
          detail={sum.link_clicks ? `${formatMoney(sum.spend / sum.link_clicks)} a click` : 'to the website'}
        />
        <Stat label="Reach" value={connected ? sum.reach.toLocaleString('en-ZA') : '–'} detail="people who saw an ad, summed by day" />
      </div>

      {connected ? (
        <Section title="Spend per day" description={`${PERIOD_LABEL[period]}. Hover a day for its spend and results.`}>
          <SpendChart days={spendByDay(adDays.data ?? [], from, to)} />
        </Section>
      ) : null}

      {connected ? (
        <Section title="Ads, best first" description="Ranked by what a conversation or lead costs. Ads with spend and nothing to show come after.">
          <AdsTable ranked={ranked} adsById={adsById} />
        </Section>
      ) : null}

      {/* From post to booking */}
      <h2 className="mt-6 mb-2.5 text-[length:var(--text-base)] font-semibold">From post to booking</h2>
      <Funnel
        steps={[
          { label: 'Posts', value: instagramTotal(instagram.data, 'posts'), detail: postsDetail(instagramTotal(instagram.data, 'posts'), daysIn(from, to)) },
          { label: 'Interactions', value: instagramTotal(instagram.data, 'interactions'), detail: 'likes, comments, saves, shares' },
          { label: 'Followers', value: latestFollowers(instagram.data), detail: followersDetail(instagram.data) },
          { label: 'Enquiries', value: enquiryList.length, detail: fromAds ? `${fromAds} from ads, ${formatMoneyWhole(costPer(sum.spend, fromAds) ?? 0)} each` : 'all channels' },
          {
            label: 'Bookings',
            value: bookingList.length,
            detail: bookedFromAds ? `${bookedFromAds} from ads, ${formatMoneyWhole(costPer(sum.spend, bookedFromAds) ?? 0)} each` : bookingValue(bookingList),
          },
        ]}
      />

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Section title="Where enquiries came from" description="What brought them. Set it on each enquiry; the ads' cost per enquiry depends on it.">
          <SourceTable enquiries={enquiryList} bookings={bookingList} />
        </Section>
        <Section title="How they reached you" description="Website form, email, WhatsApp, Instagram DM ...">
          <ChannelList enquiries={enquiryList} />
        </Section>
      </div>

      <p className="mt-2 text-[length:var(--text-2xs)] text-taupe">
        Bookings count from 16 October 2026, when the Studio started recording the day each one booked.{' '}
        <Link to="/marketing/campaigns" className="underline underline-offset-4">
          Campaigns and promotions
        </Link>{' '}
        are planned on their own page.
      </p>
    </Page>
  )
}

// ---------------------------------------------------------------------------

/** Today's budget: each ad set's (or campaign's) daily budget once, for active ads. */
function dailyBudget(ads: MetaAd[]): number {
  const budgets = new Map<string, number>()
  for (const ad of ads) {
    if (ad.status !== 'ACTIVE' || ad.daily_budget === null) continue
    budgets.set(ad.adset_id ?? ad.campaign_id ?? ad.ad_id, Number(ad.daily_budget))
  }
  return [...budgets.values()].reduce((sum, value) => sum + value, 0)
}

function instagramTotal(days: Array<{ posts: number; interactions: number | null }> | undefined, key: 'posts' | 'interactions'): number | null {
  if (!days?.length) return null
  return days.reduce((sum, day) => sum + (day[key] ?? 0), 0)
}

function latestFollowers(days: Array<{ followers: number | null }> | undefined): number | null {
  const known = (days ?? []).filter((day) => day.followers !== null)
  return known.at(-1)?.followers ?? null
}

function followersDetail(days: Array<{ followers: number | null }> | undefined): string {
  const known = (days ?? []).filter((day) => day.followers !== null)
  if (known.length < 2) return 'on Instagram'
  const gained = (known.at(-1)?.followers ?? 0) - (known[0]?.followers ?? 0)
  return `${gained >= 0 ? '+' : ''}${gained} in the period`
}

function postsDetail(posts: number | null, days: number): string {
  if (posts === null) return 'on Instagram'
  return `${(posts / Math.max(1, days / 7)).toFixed(1)} a week`
}

function bookingValue(bookings: Array<{ agreed_total: number | null }>): string {
  const value = bookings.reduce((sum, booking) => sum + Number(booking.agreed_total ?? 0), 0)
  return value ? `${formatMoneyWhole(value)} booked` : 'weddings and shoots'
}

function Stat({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="card p-4">
      <p className="label">{label}</p>
      <p className="mt-1 text-[1.5rem] font-semibold leading-tight" data-numeric>
        {value}
      </p>
      <p className="mt-0.5 text-[length:var(--text-2xs)] text-taupe">{detail}</p>
    </div>
  )
}

function ReviewCard({ review }: { review: NonNullable<ReturnType<typeof useLatestReview>['data']> }) {
  return (
    <section className="card mb-5 border-l-4 border-l-accent p-5">
      <p className="label">
        Claude's review · {formatDateShort(review.period_start)} to {formatDateShort(review.period_end)}
      </p>
      <h2 className="mt-1 text-[1.125rem] font-semibold">{review.headline}</h2>
      <div className="mt-3 grid gap-4 text-[length:var(--text-sm)] md:grid-cols-3">
        {review.working ? <ReviewPart title="What's working">{review.working}</ReviewPart> : null}
        {review.not_working ? <ReviewPart title="What isn't">{review.not_working}</ReviewPart> : null}
        {review.sweet_spot ? <ReviewPart title="Spend sweet spot">{review.sweet_spot}</ReviewPart> : null}
      </div>
      {review.recommendations.length ? (
        <div className="mt-4">
          <h3 className="label mb-1">Suggestions</h3>
          <ul className="flex list-disc flex-col gap-1 pl-5 text-[length:var(--text-sm)]">
            {review.recommendations.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="mt-2 text-[length:var(--text-2xs)] text-taupe">Nothing changes in your ads until you say yes.</p>
        </div>
      ) : null}
    </section>
  )
}

function ReviewPart({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="label mb-1">{title}</h3>
      <p className="whitespace-pre-line">{children}</p>
    </div>
  )
}

/** Daily spend as bars, one series in the accent colour, with a hover readout. */
function SpendChart({ days }: { days: Array<{ day: string; spend: number; results: number }> }) {
  const [hover, setHover] = useState<number | null>(null)
  const max = Math.max(1, ...days.map((day) => day.spend))
  const width = 720
  const height = 160
  const step = width / days.length
  const bar = Math.max(1, Math.min(18, step - 2))
  const point = hover !== null ? days[hover] : null
  const total = days.reduce((sum, day) => sum + day.spend, 0)

  if (total === 0) return <p className="text-[length:var(--text-sm)] text-taupe">No spend in this period.</p>

  return (
    <div>
      <div className="mb-1 flex h-5 items-baseline justify-between text-[length:var(--text-2xs)] text-taupe">
        <span>{formatMoneyWhole(max)} a day at most</span>
        {point ? (
          <span className="text-ink" data-numeric>
            {formatDateShort(point.day)}: {formatMoney(point.spend)}, {pluralise(point.results, 'result')}
          </span>
        ) : null}
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-40 w-full" preserveAspectRatio="none" role="img" aria-label="Spend per day" onMouseLeave={() => setHover(null)}>
        <line x1="0" x2={width} y1={height - 0.5} y2={height - 0.5} stroke="var(--color-rule)" />
        <line x1="0" x2={width} y1="0.5" y2="0.5" stroke="var(--color-rule)" strokeDasharray="3 4" />
        {days.map((day, index) => {
          const h = (day.spend / max) * (height - 4)
          const x = index * step + (step - bar) / 2
          return (
            <g key={day.day} onMouseEnter={() => setHover(index)}>
              <rect x={index * step} y="0" width={step} height={height} fill="transparent" />
              {h > 0 ? (
                <rect
                  x={x}
                  y={height - h}
                  width={bar}
                  height={h}
                  rx={Math.min(4, bar / 2)}
                  fill="var(--color-accent)"
                  opacity={hover === null || hover === index ? 1 : 0.45}
                />
              ) : null}
            </g>
          )
        })}
      </svg>
      <div className="mt-1 flex justify-between text-[length:var(--text-2xs)] text-taupe">
        <span>{formatDateShort(days[0]?.day ?? '')}</span>
        <span>{formatDateShort(days.at(-1)?.day ?? '')}</span>
      </div>
    </div>
  )
}

function AdsTable({ ranked, adsById }: { ranked: ReturnType<typeof rankAds>; adsById: Map<string, MetaAd> }) {
  if (ranked.length === 0) return <p className="text-[length:var(--text-sm)] text-taupe">No ads ran in this period.</p>
  const bestCost = ranked[0]?.costPerResult ?? null
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[44rem] text-[length:var(--text-sm)]">
        <thead>
          <tr className="text-left">
            {['Ad', 'Spent', 'Reach', 'Link clicks', 'Click rate', 'Results', 'Per result'].map((head) => (
              <th key={head} className="label border-b px-2 py-2 font-semibold">
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ranked.map((row) => {
            const ad = adsById.get(row.ad_id)
            return (
              <tr key={row.ad_id} className="border-b last:border-b-0">
                <td className="px-2 py-2">
                  <div className="flex items-center gap-2.5">
                    {ad?.thumbnail_url ? <img src={ad.thumbnail_url} alt="" className="size-9 shrink-0 rounded object-cover" /> : null}
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{ad?.name ?? row.ad_id}</p>
                      <p className="flex items-center gap-2 text-[length:var(--text-2xs)] text-taupe">
                        {ad?.campaign_name ?? ''}
                        {ad?.status === 'ACTIVE' ? <Badge tone="sage">Running</Badge> : <Badge>{ad?.status === 'PAUSED' ? 'Paused' : 'Stopped'}</Badge>}
                        {row.costPerResult !== null && row.costPerResult === bestCost ? <Badge tone="ink">Did best</Badge> : null}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-2" data-numeric>{formatMoneyWhole(row.spend)}</td>
                <td className="px-2 py-2" data-numeric>{row.reach.toLocaleString('en-ZA')}</td>
                <td className="px-2 py-2" data-numeric>{row.link_clicks.toLocaleString('en-ZA')}</td>
                <td className="px-2 py-2" data-numeric>{row.ctr !== null ? formatPercent(row.ctr, 2) : '–'}</td>
                <td className="px-2 py-2" data-numeric>{row.results}</td>
                <td className={cn('px-2 py-2', row.costPerResult === null && row.spend > 0 && 'text-clay')} data-numeric>
                  {row.costPerResult !== null ? formatMoney(row.costPerResult) : row.spend > 0 ? 'None yet' : '–'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function Funnel({ steps }: { steps: Array<{ label: string; value: number | null; detail: string }> }) {
  return (
    <ol className="grid grid-cols-2 gap-3 md:grid-cols-5">
      {steps.map((step, index) => (
        <li key={step.label} className="card relative p-4">
          <p className="label">{step.label}</p>
          <p className="mt-1 text-[1.5rem] font-semibold leading-tight" data-numeric>
            {step.value === null ? '–' : step.value.toLocaleString('en-ZA')}
          </p>
          <p className="mt-0.5 text-[length:var(--text-2xs)] text-taupe">{step.detail}</p>
          {index < steps.length - 1 ? (
            <span aria-hidden className="absolute top-1/2 -right-2.5 z-10 hidden -translate-y-1/2 text-taupe md:block">
              ›
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  )
}

function SourceTable({
  enquiries,
  bookings,
}: {
  enquiries: Array<{ lead_source: string | null }>
  bookings: Array<{ agreed_total: number | null; inquiry: { lead_source: string | null } | null }>
}) {
  const rows = new Map<string, { enquiries: number; bookings: number; value: number }>()
  const row = (key: string) => rows.get(key) ?? { enquiries: 0, bookings: 0, value: 0 }
  for (const enquiry of enquiries) {
    const key = enquiry.lead_source ?? 'unknown'
    rows.set(key, { ...row(key), enquiries: row(key).enquiries + 1 })
  }
  for (const booking of bookings) {
    const key = booking.inquiry?.lead_source ?? 'unknown'
    const current = row(key)
    rows.set(key, { ...current, bookings: current.bookings + 1, value: current.value + Number(booking.agreed_total ?? 0) })
  }
  const sorted = [...rows.entries()].sort((a, b) => b[1].enquiries - a[1].enquiries || b[1].bookings - a[1].bookings)
  if (sorted.length === 0) return <p className="text-[length:var(--text-sm)] text-taupe">No enquiries in this period.</p>
  return (
    <table className="w-full text-[length:var(--text-sm)]">
      <thead>
        <tr className="text-left">
          {['Source', 'Enquiries', 'Bookings', 'Booked'].map((head) => (
            <th key={head} className="label border-b px-2 py-2 font-semibold">
              {head}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map(([key, value]) => (
          <tr key={key} className="border-b last:border-b-0">
            <td className={cn('px-2 py-2', key === 'unknown' && 'text-taupe')}>{SOURCE_LABEL[key] ?? 'Not recorded'}</td>
            <td className="px-2 py-2" data-numeric>{value.enquiries}</td>
            <td className="px-2 py-2" data-numeric>{value.bookings}</td>
            <td className="px-2 py-2" data-numeric>{value.value ? formatMoneyWhole(value.value) : '–'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function ChannelList({ enquiries }: { enquiries: Array<{ channel: string | null }> }) {
  const counts = new Map<string, number>()
  for (const enquiry of enquiries) counts.set(enquiry.channel ?? 'unknown', (counts.get(enquiry.channel ?? 'unknown') ?? 0) + 1)
  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1])
  const max = Math.max(1, ...sorted.map(([, n]) => n))
  if (sorted.length === 0) return <p className="text-[length:var(--text-sm)] text-taupe">No enquiries in this period.</p>
  return (
    <ul className="flex flex-col gap-2.5">
      {sorted.map(([key, n]) => (
        <li key={key} className="grid grid-cols-[8rem_1fr_2rem] items-center gap-3 text-[length:var(--text-sm)]">
          <span className={cn(key === 'unknown' && 'text-taupe')}>{CHANNEL_LABEL[key] ?? 'Not recorded'}</span>
          <span className="h-2 rounded-full bg-paper">
            <span className="block h-2 rounded-full bg-accent" style={{ width: `${(n / max) * 100}%` }} />
          </span>
          <span className="text-right" data-numeric>
            {n}
          </span>
        </li>
      ))}
    </ul>
  )
}
